/**
 * NotificationDropdown Component
 * Popover showing list of notifications with mark as read functionality
 */

import React from 'react';
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

/**
 * NotificationDropdown component
 * Displays notifications in a popover with read status and actions
 */
const NotificationDropdown = ({
  notifications = [],
  anchorEl,
  onClose,
  onMarkRead,
  onMarkAllRead,
}) => {
  const open = Boolean(anchorEl);
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Get icon based on notification type
  const getIcon = (type) => {
    switch (type) {
      case 'bid_submitted':
        return <NotificationsIcon fontSize="small" sx={{ color: '#FF6B35' }} />;
      case 'new_message':
        return <EmailIcon fontSize="small" sx={{ color: '#B71C1C' }} />;
      case 'payment_confirmed':
        return <CheckCircleIcon fontSize="small" sx={{ color: '#4CAF50' }} />;
      case 'warning':
        return <WarningIcon fontSize="small" sx={{ color: '#FF9800' }} />;
      default:
        return <InfoIcon fontSize="small" sx={{ color: '#2196F3' }} />;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Box sx={{ width: 380, maxHeight: 500 }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#B71C1C',
            color: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              sx={{
                backgroundColor: '#FF6B35',
                color: '#FFFFFF',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        {/* Notifications List */}
        {notifications.length > 0 ? (
          <>
            <List sx={{ maxHeight: 350, overflow: 'auto' }}>
              {notifications.slice(0, 5).map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    button
                    onClick={() => onMarkRead(notification.id)}
                    sx={{
                      backgroundColor: notification.read
                        ? 'transparent'
                        : 'rgba(25, 118, 210, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      },
                      py: 1.5,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.read ? 400 : 600,
                            color: '#333333',
                          }}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#666666',
                            display: 'block',
                            mt: 0.5,
                            mb: 0.5,
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#999999',
                        ml: 1,
                        whiteSpace: 'nowrap',
                        fontSize: '0.7rem',
                      }}
                    >
                      {formatDistanceToNow(new Date(notification.timestamp), {
                        addSuffix: true,
                      })}
                    </Typography>
                  </ListItem>
                  {index < Math.min(4, notifications.length - 1) && <Divider />}
                </React.Fragment>
              ))}
            </List>

            <Divider />

            {/* Action Buttons */}
            <Stack direction="row" sx={{ p: 1.5, gap: 1 }}>
              <Button
                size="small"
                fullWidth
                onClick={onMarkAllRead}
                sx={{
                  color: '#B71C1C',
                  fontSize: '0.75rem',
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.05)' },
                }}
              >
                Mark All Read
              </Button>
              <Button
                size="small"
                fullWidth
                component="a"
                href="/notifications"
                sx={{
                  color: '#B71C1C',
                  fontSize: '0.75rem',
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.05)' },
                }}
              >
                View All
              </Button>
            </Stack>
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsIcon
              sx={{
                fontSize: 48,
                color: '#CCCCCC',
                mb: 1,
              }}
            />
            <Typography variant="body2" sx={{ color: '#999999' }}>
              No notifications yet
            </Typography>
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default NotificationDropdown;
